import React, { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Input from '@components/common/input';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiAdminService } from '@api';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { FormProvider } from 'react-hook-form';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { toast } from 'react-toastify';
import { useLocales } from '@hooks/helpers/useLocales';
import { convertStringsToNumbers } from '@utils/index';
import { EUserType, TLimitsSettings } from '@xeppt/xeppt-sdk/types';
import { EFundsOperation } from '@enum';
import { ELoyaltyLevel } from '@xeppt/xeppt-sdk/types/loyalty';

const paymentMethods = [
    { key: EFundsOperation.WALLET_CREDIT_TRANSFER, label: 'Send money to XEPPT wallet' },
    { key: EFundsOperation.WALLET_DEBIT_TRANSFER, label: 'Receive money from XEPPT wallet' },
    { key: EFundsOperation.WALLET_CREDIT_REQUEST, label: 'Request money by XEPPT wallet' },
    { key: EFundsOperation.WALLET_DEBIT_REQUEST, label: 'Request money from XEPPT wallet' },
    { key: EFundsOperation.WALLET_CREDIT_CARD_DEPOSIT, label: 'Load XEPPT card balance' },
    // Card-service (1_1_8) is a fixed product price set under /admin/products, not a
    // per-transaction min/max, and the backend does not enforce a limit on it — left
    // out of /admin/limits so admins don't configure a value that has no effect.
    // { key: EFundsOperation.WALLET_CREDIT_CARD_SERVICE, label: 'Order XEPPT card' },
    { key: EFundsOperation.WALLET_DEBIT_CASHBACK, label: 'Redeem cashback' },
    { key: EFundsOperation.PAY_BILL_CREDIT_PAYMENT, label: 'Pay a bill' },
    { key: EFundsOperation.E_TRANSFER_CREDIT_TRANSFER, label: 'Send money via Interac e-Transfer' },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_TRANSFER,
        label: 'Receive money via Interac e-Transfer'
    },
    {
        key: EFundsOperation.E_TRANSFER_DEBIT_REQUEST,
        label: 'Money request via Interac e-Transfer'
    },
    { key: EFundsOperation.EFT_CREDIT_WITHDRAWAL, label: 'Send money to linked bank account' },
    { key: EFundsOperation.EFT_DEBIT_DEPOSIT, label: 'Load balance from linked bank account' },
    { key: EFundsOperation.EPS_DEBIT_DEPOSIT, label: 'Load balance from linked card' },
    { key: EFundsOperation.CARD_CREDIT_WITHDRAWAL, label: 'XEPPT card transactions' },
    { key: EFundsOperation.CARD_DEBIT_DEPOSIT, label: 'XEPPT card load balance' },
    { key: EFundsOperation.GIFT_CARD_CREDIT_GIFT_CARD, label: 'Issue XEPPT gift card' },
    { key: EFundsOperation.GIFT_CARD_DEBIT_GIFT_CARD_REDEEM, label: 'Redeem XEPPT gift card' }
];
// Helper function to get only numeric values from enum
const getEnumNumericValues = (enumObject: any): number[] => {
    return Object.values(enumObject).filter((value) => typeof value === 'number') as number[];
};

const LimitsLayout = () => {
    const [activeTab, setActiveTab] = useState<EUserType>(EUserType.INDIVIDUAL);
    const [activeLevel, setActiveLevel] = useState<ELoyaltyLevel>(ELoyaltyLevel.A);
    const { requestErrorLocale } = useLocales();
    const [isEdit, setIsEdit] = useState(false);
    const form = useForm({});

    const { data: limits, handleRequest: refetchLimits } = useApiQuery({
        method: () => apiAdminService.getLimits(),
        onSuccess: (data) => {
            form.reset(data);
        }
    });

    const { handleRequest: handleUpdateSettings, isLoading } = useApiMutation({
        method: () =>
            apiAdminService.updateLimits(
                convertStringsToNumbers(form.getValues()) as TLimitsSettings
            ),
        onSuccess: () => {
            setIsEdit(false);
            toast.success('Settings were successfully updated');
            refetchLimits();
        },
        onError: requestErrorLocale
    });

    const renderFormField = (fieldName: string) => (
        <FormField
            name={fieldName}
            renderComponent={(props) => (
                <Input
                    readOnly={!isEdit}
                    onlyNumbers
                    prefix="CAD"
                    full
                    placeholder="0.00"
                    fixedAmount
                    hideErrorIcon
                    {...props}
                />
            )}
        />
    );

    const renderTable = (userType: EUserType, level: ELoyaltyLevel) => (
        <table
            className={styles.table}
            style={{
                display: activeTab === userType && activeLevel === level ? 'table' : 'none'
            }}>
            <tbody>
                <tr>
                    <td></td>
                    <td>Per transaction</td>
                    <td>Daily</td>
                    <td>Weekly</td>
                    <td>Monthly</td>
                    <td>Min amount</td>
                </tr>
                {paymentMethods.map((item) => (
                    <tr key={item.key}>
                        <td>{item.label}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.per_transfer`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.daily`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.weekly`)}</td>
                        <td>{renderFormField(`${userType}.${level}.${item.key}.monthly`)}</td>
                        <td>
                            {renderFormField(
                                `${userType}.${level}.${item.key}.amount_transfer_min`
                            )}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
    );

    // Get only numeric values from enums
    const userTypes = getEnumNumericValues(EUserType) as EUserType[];
    const loyaltyLevels = getEnumNumericValues(ELoyaltyLevel) as ELoyaltyLevel[];

    return (
        <div className={styles.wrapper}>
            <FormProvider {...form}>
                <div className={styles.header}>
                    <div className={styles.text_wrapper}>
                        <Typography variant="h4">XEPPT Limits</Typography>
                    </div>
                </div>
                <div className={styles.actions_content}>
                    <div className={styles.actions_wrapper}>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.INDIVIDUAL)}
                            variant={
                                activeTab === EUserType.INDIVIDUAL ? 'primary' : 'outlined-dark'
                            }>
                            Personal
                        </Button>
                        <Button
                            size="normal"
                            onClick={() => setActiveTab(EUserType.BUSINESS)}
                            variant={
                                activeTab === EUserType.BUSINESS ? 'primary' : 'outlined-dark'
                            }>
                            Business
                        </Button>
                    </div>
                    <div className={styles.actions}>
                        {!isEdit && (
                            <Button
                                size="normal"
                                variant="outlined"
                                leftIcon="edit"
                                onClick={() => setIsEdit(true)}>
                                Edit
                            </Button>
                        )}
                        {isEdit && (
                            <>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsEdit(false);
                                        form.reset(limits);
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleUpdateSettings}
                                    isLoading={isLoading}
                                    size="normal"
                                    variant="primary">
                                    Save Changes
                                </Button>
                            </>
                        )}
                    </div>
                </div>
                <div className={styles.content}>
                    <div className={styles.block}>
                        <div className={styles.divider} />
                        <div className={styles.actions_wrapper}>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.A)}
                                variant={
                                    activeLevel === ELoyaltyLevel.A ? 'primary' : 'outlined-dark'
                                }>
                                Silver
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.B)}
                                variant={
                                    activeLevel === ELoyaltyLevel.B ? 'primary' : 'outlined-dark'
                                }>
                                Gold
                            </Button>
                            <Button
                                size="normal"
                                onClick={() => setActiveLevel(ELoyaltyLevel.C)}
                                variant={
                                    activeLevel === ELoyaltyLevel.C ? 'primary' : 'outlined-dark'
                                }>
                                Platinum
                            </Button>
                        </div>

                        {/* Render all possible combinations but only show the active one */}
                        {userTypes.map((userType) =>
                            loyaltyLevels.map((level) => (
                                <div key={`${userType}-${level}`}>
                                    {renderTable(userType, level)}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </FormProvider>
        </div>
    );
};

export default LimitsLayout;
